/**
 * Hyperframes port of `src/scenes/outro/logoOutro.tsx`.
 * Logo lockup + tagline + URL, with cinematic backdrop and outro fade.
 */
import type { SceneBuilder } from "../../reel/scenes.js";
import { brandBackdrop } from "../../primitives/brandBackdrop.js";
import { escapeHtml } from "../../composition.js";
import { typeSize, accentFor, makeTl, clipOutroFade, combineSceneFragments, weights, withAlpha, colors as themeColors } from "../../reel/sceneShared.js";
import { brandTagline, brandUrl, ci } from "../../../brand/theme.js";

const SUPPORTED_IMG_EXT = [".png", ".jpg", ".jpeg", ".webp", ".svg"];

function resolveLogoSrc(): string | null {
  const logoPath = ci.logo ?? "src/brand/logo.svg";
  const ext = logoPath.toLowerCase().slice(logoPath.lastIndexOf("."));
  if (!SUPPORTED_IMG_EXT.includes(ext)) return null;
  const filename = logoPath.split("/").pop() ?? "logo.svg";
  return `assets/brand/${filename}`;
}

export const logoOutro: SceneBuilder = ({ ctx, clipPrefix, inputs, clipDurationSec, clipStartSec }) => {
  const accent = accentFor(inputs);
  const tagline = inputs.text ?? brandTagline ?? "";
  const url = (inputs.url ?? brandUrl ?? "").replace(/^https?:\/\//, "");

  const lgSize = typeSize("lg", ctx.format);
  const mdSize = typeSize("md", ctx.format);

  const t = makeTl(clipStartSec);
  const wrapId = `${clipPrefix}-wrap`;
  const logoBoxId = `${clipPrefix}-logobox`;
  const tagId = `${clipPrefix}-tag`;
  const urlId = `${clipPrefix}-url`;

  const logoSrc = resolveLogoSrc();
  const logoH = ctx.format === "9:16" ? 280 : 220;

  const backdrop = brandBackdrop(ctx, `${clipPrefix}-bg`, { variant: "cinematic", accent });

  const logoMarkup = logoSrc
    ? `<img src="${escapeHtml(logoSrc)}" alt="logo" style="height:${logoH}px;width:auto;object-fit:contain;" />`
    : `<div style="width:${logoH}px;height:${logoH}px;border-radius:9999px;background:${themeColors.fg};"></div>`;

  const html = `<div id="${wrapId}" class="clip" data-start="${clipStartSec.toFixed(4)}" data-duration="${clipDurationSec.toFixed(4)}" style="position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:40px;padding:96px;">
  <div id="${logoBoxId}" style="filter:drop-shadow(0 0 80px ${withAlpha(themeColors.fg, 0.4)});">
    ${logoMarkup}
  </div>
  ${tagline ? `<div id="${tagId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.semibold};font-size:${lgSize * 0.55}px;letter-spacing:-0.4px;color:${themeColors.fg};text-align:center;max-width:${ctx.format === "9:16" ? 820 : 1200}px;">${escapeHtml(tagline)}</div>` : ""}
  ${url ? `<div id="${urlId}" style="font-family:'Inter',sans-serif;font-weight:${weights.semibold};font-size:${mdSize * 0.55}px;color:${withAlpha(themeColors.fg, 0.85)};letter-spacing:-0.4px;">${escapeHtml(url)}</div>` : ""}
</div>`;

  const initLines: string[] = [
    `gsap.set("#${logoBoxId}", { scale: 0.85, opacity: 0 });`,
  ];
  if (tagline) initLines.push(`gsap.set("#${tagId}", { opacity: 0, y: 14 });`);
  if (url) initLines.push(`gsap.set("#${urlId}", { opacity: 0 });`);

  const tweens: string[] = [
    t.to(`#${logoBoxId}`, { scale: 1, opacity: 1, duration: 0.93, ease: "raw:\"power2.out\"" }, 0),
  ];
  if (tagline) tweens.push(t.to(`#${tagId}`, { opacity: 1, y: 0, duration: 0.53, ease: "raw:\"power1.out\"" }, 14 / 30));
  if (url) tweens.push(t.to(`#${urlId}`, { opacity: 1, duration: 0.47, ease: "raw:\"power1.out\"" }, 22 / 30));
  tweens.push(clipOutroFade(clipPrefix, clipDurationSec, clipStartSec, 8));

  return combineSceneFragments([backdrop, { html, init: initLines.join("\n"), tweens }]);
};
